import { useCallback, useState } from 'react'
import { saveAs } from 'file-saver'
import API from '@api/api'

const loadImage = (src) => {
    return new Promise((resolve, reject) => {
        const img = new Image()
        img.crossOrigin = 'anonymous'
        img.onload = () => resolve(img)
        img.onerror = reject
        img.src = src
    })
}

const canvasToBlob = (canvas, mimeType = 'image/png', quality = 0.92) => {
    return new Promise((resolve) => {
        if (canvas.toBlob) {
            canvas.toBlob((blob) => resolve(blob), mimeType, quality)
            return
        }
        resolve(null)
    })
}

const useDownloadLongDetailImage = () => {
    const [loading, setLoading] = useState(false)
    const [progress, setProgress] = useState(0)
    const [error, setError] = useState('')

    const normalizeDownloadItems = (items = []) => {
        const dedup = new Map()
        ;(items || []).forEach((item) => {
            if (!item) return
            if (typeof item === 'string') {
                const baseTaskId = item.split('_')?.[0] || item
                if (!baseTaskId) return
                dedup.set(`normal_${baseTaskId}`, { taskId: baseTaskId })
                return
            }
            if (typeof item === 'object') {
                const baseTaskId = (item.taskId || '').split('_')?.[0] || item.taskId
                const imageId = item.imageId
                if (!baseTaskId) return
                const key = imageId ? `batch_${baseTaskId}_${imageId}` : `normal_${baseTaskId}`
                dedup.set(key, imageId ? { taskId: baseTaskId, imageId } : { taskId: baseTaskId })
            }
        })
        return [...dedup.values()]
    }

    const downloadLongImage = useCallback(async (downloadItems = [], options = {}) => {
        const {
            targetWidth = 1000,
            fileName = `详情页长图_${Date.now()}.png`,
            mimeType = 'image/png',
            quality = 0.92,
            gap = 0,
            background = '#ffffff',
        } = options

        const validItems = normalizeDownloadItems(downloadItems)
        if (!validItems.length) {
            setError('没有可下载图片')
            return false
        }

        setLoading(true)
        setProgress(0)
        setError('')

        try {
            const urls = []
            for (let i = 0; i < validItems.length; i += 1) {
                const currentItem = validItems[i]
                const res = currentItem?.imageId
                    ? await API.batchCommonDownload({ taskId: currentItem.taskId, imageId: currentItem.imageId })
                    : await API.commonDownload({ taskId: currentItem.taskId })
                if (res?.code !== 0 || !res?.data) throw new Error(res?.message || '无水印地址获取失败')
                urls.push(res.data)
                setProgress(Math.round(((i + 1) / validItems.length) * 60))
            }

            const images = await Promise.all(urls.map((url) => loadImage(url)))
            const imageHeights = images.map((img) => {
                const ratio = img.height / img.width || 1
                return Math.round(targetWidth * ratio)
            })
            const totalHeight = imageHeights.reduce((sum, h) => sum + h, 0) + gap * (images.length - 1)

            const canvas = document.createElement('canvas')
            canvas.width = targetWidth
            canvas.height = totalHeight
            const ctx = canvas.getContext('2d')
            if (!ctx) throw new Error('创建画布失败')

            ctx.fillStyle = background
            ctx.fillRect(0, 0, canvas.width, canvas.height)

            let currentY = 0
            images.forEach((img, index) => {
                const drawHeight = imageHeights[index]
                ctx.drawImage(img, 0, currentY, targetWidth, drawHeight)
                currentY += drawHeight + gap
            })

            setProgress(90)
            const blob = await canvasToBlob(canvas, mimeType, quality)
            if (blob) {
                saveAs(blob, fileName)
            } else {
                const dataUrl = canvas.toDataURL(mimeType, quality)
                saveAs(dataUrl, fileName)
            }
            setProgress(100)
            return true
        } catch (err) {
            setError(err?.message || '下载详情页长图失败')
            return false
        } finally {
            setLoading(false)
        }
    }, [])

    return {
        downloadLongImage,
        loading,
        progress,
        error,
    }
}

export default useDownloadLongDetailImage
